import { computed, ref } from 'vue'
import { supabase } from './supabase/client'
import {
  controlledFeature,
  defaultDeploymentSettings,
  deploymentCatalog,
  type DeploymentFeature,
  type DeploymentSetting,
} from '../../../supabase/functions/_shared/deployment_catalog'

export { deploymentCatalog }
export type { DeploymentFeature, DeploymentSetting }
export const isCloudflareDeployment = window.__APP_CONFIG__?.DEPLOYMENT_PROFILE === 'cloudflare'
export const deploymentSettings = ref<DeploymentSetting[]>(defaultDeploymentSettings())
export const deploymentError = ref('')
export const disabledDeploymentSettings = computed(() =>
  isCloudflareDeployment ? deploymentSettings.value.filter((row) => !row.enabled) : [],
)
export function featureEnabled(feature: DeploymentFeature) {
  return !isCloudflareDeployment || deploymentSettings.value.find((row) => row.feature === feature)?.enabled === true
}
export function featureReason(feature: DeploymentFeature) {
  return deploymentSettings.value.find((row) => row.feature === feature)?.reason || deploymentCatalog[feature].reason
}

async function requestConfig(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('deployment-config', {
    body,
    signal: AbortSignal.timeout(10000),
  })
  if (error || !Array.isArray(data?.data)) throw new Error('部署功能配置读取或保存失败，请检查后端部署与权限。')
  const rows = data.data as DeploymentSetting[]
  if (
    defaultDeploymentSettings().some(
      (f) =>
        !rows.some(
          (row) => row.feature === f.feature && typeof row.enabled === 'boolean' && typeof row.reason === 'string',
        ),
    )
  ) {
    throw new Error('部署功能配置格式不完整。')
  }
  deploymentSettings.value = rows
  deploymentError.value = ''
  return rows
}
let pending: Promise<DeploymentSetting[]> | null = null
export function refreshDeploymentSettings() {
  if (pending) return pending
  pending = requestConfig({ action: 'read' })
    .catch((error) => {
      deploymentSettings.value = defaultDeploymentSettings()
      deploymentError.value = error.message
      throw error
    })
    .finally(() => {
      pending = null
    })
  return pending
}
export async function saveDeploymentSetting(setting: DeploymentSetting) {
  if (pending) await pending.catch(() => undefined)
  return requestConfig({ action: 'save', ...setting })
}
export async function ensureDeploymentFeature(feature: DeploymentFeature) {
  if (!isCloudflareDeployment) return
  await refreshDeploymentSettings().catch(() => undefined)
  if (!featureEnabled(feature)) throw new Error(featureReason(feature))
}
export async function guardDeploymentOperation(name: string, body: Record<string, unknown>) {
  const feature = controlledFeature(name, body)
  if (feature) await ensureDeploymentFeature(feature)
  if (name === 'media-learning' && body.action === 'reserve-upload' && body.translationEnabled)
    await ensureDeploymentFeature('media_translation')
}
